import { createProductId } from 'helpers'

import { ISubscription } from 'models/subscriptions.model'

export const enum GTM_EVENTS {
  BEGIN_CHECKOUT = 'begin_checkout',
  ADD_TO_CART = 'add_to_cart',
  PURCHASE = 'purchase',
  FIRST_PAGE_COMPLETED = 'first_page_completed',
}

export const enum GTM_EVENT_CATEGORIES {
  TRANSITION = 'transition',
}

class GoogleAnalyticsService {
  private eventsCache: Map<string, string> = new Map()

  logCheckoutStarted({
    subscription,
    email,
    uuid,
    currency = 'USD',
  }: {
    subscription: ISubscription
    email: string
    uuid: string
    currency: string
  }): void {
    const isAlreadyLogged = this.eventsCache.has(GTM_EVENTS.BEGIN_CHECKOUT)
    if (!window.dataLayer || isAlreadyLogged || !subscription) return

    const {
      mainPrices: { periodName, periodQuantity, fullPrice },
      trialPrices: { fullPrice: trialPrice },
    } = subscription

    this.eventsCache.set(GTM_EVENTS.BEGIN_CHECKOUT, '')
    window.dataLayer.push({
      event: GTM_EVENTS.BEGIN_CHECKOUT,
      email,
      user_id: uuid,
      ecommerce: {
        value: trialPrice || fullPrice,
        currency,
        items: [
          {
            id: createProductId({
              periodName,
              periodQuantity,
              price: fullPrice || trialPrice,
            }),
            price: fullPrice || trialPrice,
          },
        ],
      },
    })
  }

  logAddingToCart({
    subscription,
    email,
    uuid,
  }: {
    subscription: ISubscription
    email: string
    uuid: string
  }): void {
    if (!window.dataLayer || !subscription) return

    const {
      mainPrices: { periodName, periodQuantity, fullPrice },
      trialPrices: { fullPrice: trialPrice },
    } = subscription

    window.dataLayer.push({
      event: GTM_EVENTS.ADD_TO_CART,
      email,
      user_id: uuid,
      ecommerce: {
        items: [
          {
            id: createProductId({
              periodName,
              periodQuantity,
              price: fullPrice || trialPrice,
            }),
            price: fullPrice || trialPrice,
          },
        ],
      },
    })
  }

  logPurchaseCompleted({
    subscriptionId,
    price,
    currency = 'USD',
    email,
    uuid,
    subscription,
    isUpsell = false,
  }: {
    subscriptionId: string
    price: number
    currency?: string
    email: string
    uuid: string
    subscription: ISubscription | null
    isUpsell?: boolean
  }): void {
    if (!window.dataLayer || !subscription) return

    const {
      mainPrices: { periodName, periodQuantity },
    } = subscription

    window.dataLayer.push({
      event: GTM_EVENTS.PURCHASE,
      is_fp: !isUpsell,
      email,
      user_id: uuid,
      ecommerce: {
        transaction_id: subscriptionId,
        value: price,
        currency,
        items: [
          {
            id: createProductId({
              periodName,
              periodQuantity,
              price,
            }),
            price,
          },
        ],
      },
    })
  }

  logFirstPageCompleted(): void {
    if (!window.dataLayer) return

    window.dataLayer.push({
      event: GTM_EVENTS.FIRST_PAGE_COMPLETED,
      event_category: GTM_EVENT_CATEGORIES.TRANSITION,
    })
  }
}

export const googleAnalyticsLogger = new GoogleAnalyticsService()
